import type { ReservedKeyContract } from "@/api";
import { labelGuideSummary } from "./labelGuide";

// reserved key → 입력 컨트롤 매핑(프론트 product 결정). 허용 값과 정책은 백엔드
// 계약(GET /v1/label-reserved-keys)이 SoT 다. 여기서는 "같은 reserved 라벨을 어떤 UI 로
// 입력받을지" 만 정한다. 하드웨어 축(embodiment / arms / end_effector / sensor / camera_*)은
// Embodiment 카탈로그가 소유한다. 여기에는 reserved 라벨 facet(environment / source / task)만
// 둔다.
export type ReservedControlKind = "singleDropdown" | "switcher" | "openMultiSelect";

type ReservedKeyUi = {
  label: string;
  control: ReservedControlKind;
};

export const RESERVED_KEY_UI: Record<string, ReservedKeyUi> = {
  environment: { label: "환경", control: "singleDropdown" },
  source: { label: "출처", control: "singleDropdown" },
  task: { label: "작업", control: "openMultiSelect" },
};

// 업로드 모달에 표시하는 reserved label key. 업로더가 선언하는 환경과 출처다. 하드웨어는
// embodiment picker 가 담당한다. 순서는 입력 순서다(product 결정). 표시 여부는 product 결정이라
// 상수로 두고, 필수 여부(required)는 계약에서 계산한다.
export const UPLOAD_RESERVED_KEYS: readonly string[] = ["environment", "source"];

// 업로드 게이트에서 강제하는 reserved key. 계약의 `upload_required` 가 SoT 다.
// FE 는 backend 필수 목록을 복사해 하드코딩하지 않는다.
export function uploadRequiredKeys(contracts: readonly ReservedKeyContract[]): string[] {
  return contracts.filter((contract) => contract.upload_required).map((contract) => contract.key);
}

// open 정책 key(task)는 카탈로그 제안값을 채운다.
export const OPEN_RESERVED_KEYS: readonly string[] = ["task"];

export type ReservedSelections = Record<string, readonly string[]>;

export type ReservedAssignment = {
  key: string;
  value: string;
};

export function reservedKeyLabel(key: string): string {
  return RESERVED_KEY_UI[key]?.label ?? key;
}

// snake_case allowlist 값을 사람이 읽기 쉬운 라벨로 바꾼다. (single_arm → "Single arm")
export function formatReservedValue(value: string): string {
  const spaced = value.replace(/_/g, " ").trim();
  if (spaced === "") {
    return value;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function reservedControlKind(key: string): ReservedControlKind | null {
  return RESERVED_KEY_UI[key]?.control ?? null;
}

export type ReservedValueOption = {
  value: string;
  label: string;
  /** 라벨 가이드 문구. ContentSwitcher 가 옵션 hover/focus 툴팁으로 표시한다. */
  tooltip?: string;
  /** 같은 가이드 문구. 드롭다운은 tooltip 을 읽지 않고 항목 아래 설명으로 표시한다. */
  description?: string;
};

// reserved allowlist 값 목록 → 선택 컨트롤(switcher/dropdown) 옵션. 가이드가 있는 값은 가이드를
// 함께 넣어 사용자가 고르기 전에 의미를 알게 한다. 가이드 SoT 는 labelGuide.ts 다(칩 hover 와 같은
// 문구). 컨트롤마다 읽는 필드가 달라 같은 문구를 두 필드에 넣는다.
export function reservedValueOptions(
  key: string,
  values: readonly string[] | undefined,
): ReservedValueOption[] {
  return (values ?? []).map((value) => {
    const guide = labelGuideSummary(key, value) ?? undefined;
    return { value, label: formatReservedValue(value), tooltip: guide, description: guide };
  });
}

// 선택이 빈(필수 미충족) reserved key 목록. 소비처가 업로드·활성화 게이트에서 강제한다.
export function findMissingRequiredKeys(
  requiredKeys: readonly string[],
  selections: ReservedSelections,
): string[] {
  return requiredKeys.filter((key) => (selections[key]?.length ?? 0) === 0);
}

// 선택을 (key, value) 부여 목록으로 평탄화한다. (key,value) assign 과 reserved reconcile 에 쓴다.
export function selectionsToAssignments(selections: ReservedSelections): ReservedAssignment[] {
  const assignments: ReservedAssignment[] = [];
  for (const [key, values] of Object.entries(selections)) {
    for (const value of values) {
      assignments.push({ key, value });
    }
  }
  return assignments;
}

// 부여된 라벨 중 `keys` 범위의 reserved 라벨을 selections(key→values)로 모은다. 리뷰 예약
// 라벨 단계의 prefill 과 완료 판정에 쓴다(DatasetReviewItem.labels 등 확정 라벨 입력).
export function selectionsFromLabels(
  labels: readonly { key: string; value: string }[],
  keys: readonly string[],
): ReservedSelections {
  const result: Record<string, string[]> = {};
  for (const label of labels) {
    if (!keys.includes(label.key)) {
      continue;
    }
    (result[label.key] ??= []).push(label.value);
  }
  return result;
}

// contract 목록을 key→contract 로 인덱싱한다.
export function indexContractsByKey(
  contracts: readonly ReservedKeyContract[],
): Map<string, ReservedKeyContract> {
  return new Map(contracts.map((contract) => [contract.key, contract]));
}
